import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, NEUTRAL } from "../../../theme/tokens";
import { AccentIconTile } from "./AccentIconTile";

type Props = {
	/** Leading glyph, rendered inside a brand `AccentIconTile` (xs). */
	icon: ReactNode;
	/**
	 * The tile's glyph colour and wash, for a panel whose identity is a status rather than the brand (a
	 * warning panel takes `palette.warning`). Passed straight to `AccentIconTile`'s `color` / `tint`, so
	 * pass theme-derived values. Absent, the tile is the brand tile, as before.
	 */
	tileColor?: string;
	tileTint?: string;
	/** Panel title, rendered at `subtitle2` weight. */
	title: ReactNode;
	/**
	 * `id` on the title element, so the panel around the header can name itself with
	 * `aria-labelledby` (a `role="dialog"` popover). Absent, the title carries no id, as before.
	 */
	titleId?: string;
	/**
	 * One quiet line under the title saying what the panel is FOR. Optional and additive: a header
	 * without one is unchanged, and a header with one stops the explanation from having to live in the
	 * panel body (or, worse, nowhere).
	 */
	subtitle?: ReactNode;
	/**
	 * Right-aligned trailing slot (a count, a chip, an action cluster). Rendered
	 * INDEPENDENTLY — unlike `CommonHeader`, there is no close-button coupling gating it,
	 * and it wraps onto its own line beneath the title on a narrow panel.
	 */
	endingContent?: ReactNode;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * A lightweight header row for a working panel/card: a brand `AccentIconTile` + a
 * `subtitle2` title, with an independent right-aligned `endingContent` slot. This is the
 * tile + subtitle-title pattern that had been hand-rebuilt across panel headers.
 *
 * Deliberately NOT `CommonHeader`: that one hardcodes a 20px title and only renders its
 * `endingContent` when an `onClose` is passed (the two are coupled). `PanelHeader` keeps the
 * title at the `subtitle2` scale a panel wants and treats `endingContent` as a first-class,
 * always-rendered slot. The caller owns the surrounding container (background, padding,
 * border) so this stays purely the content row.
 */
export const PanelHeader: FC<Props> = ({ icon, tileColor, tileTint, title, titleId, subtitle, endingContent, dataTestId, sx }) => {
	return (
		<Box
			data-testid={dataTestId}
			sx={[
				{ display: "flex", alignItems: subtitle ? "flex-start" : "center", flexWrap: "wrap", columnGap: 2, rowGap: 1, width: "100%" },
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box sx={{ display: "flex", alignItems: subtitle ? "flex-start" : "center", gap: 1.25, minWidth: 0 }}>
				<AccentIconTile size="xs" color={tileColor} tint={tileTint}>
					{icon}
				</AccentIconTile>
				<Box sx={{ minWidth: 0 }}>
					<Typography id={titleId} variant="subtitle2" noWrap sx={{ fontWeight: "bold", color: "text.primary", minWidth: 0 }}>
						{title}
					</Typography>
					{subtitle && <Typography sx={{ mt: 0.25, fontSize: FONT_SIZE.SECONDARY, color: NEUTRAL[500], lineHeight: 1.4 }}>{subtitle}</Typography>}
				</Box>
			</Box>
			{endingContent && <Box sx={{ display: "flex", alignItems: "center", ml: "auto", minWidth: 0 }}>{endingContent}</Box>}
		</Box>
	);
};
